import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { IoIosInformationCircle } from "react-icons/io";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { TypographyH2 } from "@/components/typography/TypographyH2";
import axiosInstance from "@/api/axios";
import { useCallback, useEffect, useState, useMemo } from "react";
import toastService from "@/services/toastService";
import { getStages, Stage } from "@/features/kanban/services/KanbanService";
import { Lead } from "@/pages/Leads/utils/types";
import { useLoading } from "@/contexts/LoadingContext";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
// import { Value } from "@radix-ui/react-select";
export const formSchema = z.object({
  name: z
    .string({ required_error: "Name is required" })
    .min(1, { message: "Name is required" }),
  // expected_revenue: z.number().optional(),
  // cost_of_construction: z.number().optional(),
  // name: z.string().optional(),
  expected_revenue: z.string().optional(),
  cost_of_construction: z.string().optional(),
  stage_id: z.string().optional(),
  project_class_id: z.string().optional(),
  region_id: z.string().optional(),
  county_id: z.string().optional(),
  project_type_id: z.string().optional(),
  project_description: z.string().optional(),
  city_id: z.string().optional(),
  address: z.string().optional(),
  country_id: z.string().optional(),
  province_id: z.string().optional(),
  latitude: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (!value) return true;
        const lat = parseFloat(value);
        return lat >= -90 && lat <= 90;
      },
      {
        message: "Latitude must be between -90 and 90",
      }
    ),
  longitude: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (!value) return true;
        const lon = parseFloat(value);
        return lon >= -180 && lon <= 180;
      },
      {
        message: "Longitude must be between -180 and 180",
      }
    ),
  permit_issue_date: z.string().optional(),
  project_status_id: z.string().optional(),

  additional_info: z.string().optional(),
  owner_company: z.string().optional(),
  owner_name: z.string().optional(),
  owner_phone: z.string().optional(),
  owner_email: z.string().optional(),
  applicant_company: z.string().optional(),
  applicant_name: z.string().optional(),
  applicant_phone: z.string().optional(),
  applicant_email: z.string().optional(),
});

const formLabelStyle = "text-[14px] font-medium text-[#616161] capitalize";

export type MasterDataParam = {
  countries?: boolean;
  countryId?: number;
  provinces?: boolean;
  provinceId?: number;
  projectClasses?: boolean;
  projectTypes?: boolean;
  projectStatuses?: boolean;
  counties?: boolean;
  countyId?: number;
  regions?: boolean;
  regionId?: number;
  cities?: boolean;
};

export type MasterData = {
  countries: {
    code: string;
    currency_id: [number, string];
    display_name: string;
    id: number;
    name: string;
  }[];
  provinces: {
    country_id: [number, string];
    id: number;
    name: string;
  }[];
  projectClasses: {
    id: number;
    name: string;
  }[];
  projectTypes: {
    project_class_id: [number, string];
    id: number;
    name: string;
  }[];
  projectStatuses: {
    id: number;
    name: string;
  }[];
  regions: {
    id: number;
    name: string;
    state_id: [number, string];
  };
  counties: {
    id: number;
    name: string;
    state_id: [number, string];
    region_id: [number, string];
  };
  cities: {
    id: number;
    name: string;
    state_id?: [number, string];
    region_id?: [number, string];
    county_id?: [number, string];
  };
};

export const getMasterData = async (param: object) => {
  // setIsLoading(true);
  try {
    const response = await axiosInstance({
      method: "GET",
      url: "/odoo/api/v1/crm/masterData",
      params: param,
    });
    console.log("response", response);
    // setColumns(response.data.data.data.stages);
    return response.data.data;
  } catch (err) {
    console.log("err", err);
  } finally {
    // setIsLoading(false);
  }
};

export const createLead = async (body: object) => {
  // setIsLoading(true);
  try {
    const response = await axiosInstance({
      method: "POST",
      url: "/odoo/api/v1/crm/leads/",
      data: body,
    });
    console.log("response", response);
    // setColumns(response.data.data.data.stages);
    return response.data;
  } catch (err) {
    console.log("err", err);
  } finally {
    // setLoading(false);
  }
};

type addLeadProps = {
  lead?: Lead;
};

const AddLeadForm: React.FC<addLeadProps> = ({ lead }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const stageId = searchParams.get("stage")?.toString() || "";
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: useMemo(() => {
      if (lead) {
        return {
          name: lead?.builder?.company_details?.name
            ? lead.builder.company_details.name
            : lead?.applicant_company
              ? lead.applicant_company
              : lead?.owner_company
                ? lead.owner_company
                : lead?.address
                  ? lead.address
                  : lead?.display_name || "",
          permit_issue_date: lead?.permit_issue_date || "",
          city_id: lead?.city_id?.[0]?.toString() || "",
          expected_revenue: String(lead?.expected_revenue),
          cost_of_construction: String(lead?.cost_of_construction),
          address: lead?.address || "",
          country_id: lead?.country_id?.[0]?.toString() || "",
          province_id: lead?.province_id?.[0]?.toString() || "",
          latitude: lead?.latitude?.toString() || "",
          longitude: lead?.longitude?.toString() || "",
          project_description: lead?.project_description?.toString() || "",
          project_class_id: lead?.project_class_id?.[0]?.toString() || "",
          project_type_id: lead?.project_type_id?.[0]?.toString() || "",
          region_id: lead?.region_id?.[0]?.toString() || "",
          county_id: lead?.county_id?.[0]?.toString() || "",
          project_status_id: lead?.project_status_id?.[0]?.toString() || "",
          stage_id: stageId || "",
        };
      }
      return {};
    }, [lead]), // Ensure form re-initializes when lead changes
  });

  const editLead = async (numId: number | undefined, body: object) => {
    console.log(body, "line 183");
    // delete body.stage_id;
    // setLoading(true);
    try {
      const response = await axiosInstance({
        method: "PUT",
        url: `/odoo/api/v1/crm/leads/`,
        data: {
          lead_id: numId,
          // updates:body
          updates: body,
        },
      });
      console.log("response", response);
      return response.data;
    } catch (err) {
      console.log("err", err);
    } finally {
      // setLoading(false);
    }
  };

  const location = useLocation();
  const isEditMode = location.pathname.includes("edit");
  const isCreateMode = location.pathname.includes("add_lead");
  const user = useSelector(
    (state: RootState) => state.rootReducer.userReducer.user
  );
  console.log("line 239", user);

  useEffect(() => {
    console.log("line 243", user?.countryName);
    if (isEditMode && lead) {
      console.log("Resetting form with lead:", lead);

      const getSanitizedValues = () => {
        return Object.fromEntries(
          Object.entries(lead).map(([key, value]) => {
            if (typeof value === "boolean" && value === false) return [key, ""];

            if (
              [
                "province_id",
                "project_class_id",
                "project_type_id",
                "project_status_id",
                "stage_id",
                "region_id",
                "county_id",
                "city_id", // Ensure it's always checked
              ].includes(key)
            ) {
              if (Array.isArray(value) && value.length > 0) {
                return [key, value[0]?.toString()];
              } else {
                return [key, ""]; // Ensure it's included even if empty
              }
            }

            if (
              key === "country_id" &&
              Array.isArray(value) &&
              value.length > 0
            ) {
              return [key, value.at(0)?.toString() ?? ""];
            }

            if (
              ["longitude", "latitude"].includes(key) &&
              value !== undefined
            ) {
              return [key, value.toString()];
            }

            if (["expected_revenue", "cost_of_construction"].includes(key)) {
              return [key, String(value) || 0];
            }

            return [key, value];
          })
        );
      };

      const sanitizedValues = getSanitizedValues();

      //  Ensure 'name' field gets the correct title
      sanitizedValues.name = lead?.builder?.company_details?.name
        ? lead.builder.company_details.name
        : lead?.applicant_company
          ? lead.applicant_company
          : lead?.owner_company
            ? lead.owner_company
            : lead?.address
              ? lead.address
              : lead?.display_name || "";
      sanitizedValues.name =
        [
          lead?.builder?.company_details?.name
            ? lead.builder.company_details.name
            : lead?.applicant_company
              ? lead.applicant_company
              : lead?.owner_company
                ? lead.owner_company
                : lead?.address
                  ? lead.address
                  : lead?.display_name,
        ].find((val): val is string => typeof val === "string") || "";

      console.log("Sanitized Values:", sanitizedValues);
      form.reset(sanitizedValues);
      form.setValue("name", sanitizedValues.name);
    }
  }, [isEditMode, lead]);

  const [masterData, setMasterData] = useState<Partial<MasterData>>();
  const [stages, setStages] = useState<Stage[]>([]);

  //   const [imageUrls, setImageUrls] = useState<string[]>([""]);

  const { setLoading } = useLoading();

  const navigate = useNavigate();

  const [provinceOptions, setProvinceOptions] = useState<
    {
      country_id: [number, string];
      id: number;
      name: string;
    }[]
  >();

  const [filteredProjectTypes, setFilteredProjectTypes] = useState<
    {
      project_class_id: [number, string];
      id: number;
      name: string;
    }[]
  >();

  const [regionOptions, setRegionOptions] = useState<
    {
      id: number;
      name: string;
      state_id: [number, string];
    }[]
  >([]);

  const [countyOptions, setCountyOptions] = useState<
    {
      id: number;
      name: string;
      state_id: [number, string];
      region_id: [number, string];
    }[]
  >([]);

  const [cityOptions, setCityOptions] = useState<
    {
      id: number;
      name: string;
      state_id: [number, string];
      region_id: [number, string];
      county_id: [number, string];
    }[]
  >([]);

  const getRegionsForProvince = useCallback(
    async (provinceId: string) => {
      if (provinceId) {
        try {
          const data = await getMasterData({
            provinceId: Number(provinceId),
            regions: true,
            cities: true,
          });

          // Check if the response contains regions and update the state
          if (data?.regions || data?.cities) {
            setRegionOptions(data.regions);
            setCityOptions(data.cities);
          } else {
            setRegionOptions([]); // Set empty array if no regions are returned
          }
        } catch (error) {
          console.error("Error fetching regions:", error);
          setRegionOptions([]); // Set empty array in case of error
        }
      } else {
        setRegionOptions([]); // Clear regions if no provinceId
      }
    },
    [] // No dependencies needed, can be added if masterData changes
  );

  const getCountiesForProvinceAndRegion = useCallback(
    async (province_id: string, region_id: string) => {
      if (province_id && region_id) {
        const data = await getMasterData({
          provinceId: Number(province_id),
          regionId: Number(region_id),
          counties: true,
          cities: true,
        });

        if (data?.counties || data?.cities) {
          setCountyOptions(data.counties);
          setCityOptions(data.cities);
        } else {
          setCountyOptions([]);
        }
      } else {
        setCountyOptions([]);
      }
    },
    []
  );

  const cityId = form.watch("city_id");
  console.log("line 475", cityId);

  useEffect(() => {
    if (lead?.province_id && masterData?.regions) {
      getRegionsForProvince(lead.province_id[0]?.toString());
    }

    if (lead?.province_id && lead?.region_id && masterData?.regions) {
      getCountiesForProvinceAndRegion(
        lead.province_id[0]?.toString(),
        lead.region_id[0]?.toString()
      );
    }
  }, [
    lead?.province_id,
    lead?.region_id,
    getRegionsForProvince,
    masterData?.regions,
  ]);

  // const handleProvinceChange = (province_id: string) => {
  //   form.setValue("province_id", province_id);
  //   getRegionsForProvince(province_id);
  //   form.setValue("region_id", ""); // Clear dependent fields
  //   form.setValue("county_id", "");
  // };

  // const handleRegionChange = (region_id: string) => {
  //   const province_id = form.watch("province_id");
  //   form.setValue("region_id", region_id);
  //   form.setValue("county_id", "");
  //   if(province_id)
  //   {
  //     getCountiesForProvinceAndRegion(province_id, region_id);
  //   }

  // };

  const getProjectTypesForClass = useCallback(
    async (project_class_id: string) => {
      if (masterData?.projectTypes && project_class_id) {
        const filtered = masterData.projectTypes.filter(
          (type) => type.project_class_id[0].toString() === project_class_id
        );
        setFilteredProjectTypes(filtered || []);
      } else {
        setFilteredProjectTypes([]); // If no project class is selected, clear project types
      }
    },
    [masterData?.projectTypes]
  );

  const handleProjectClassChange = (project_class_id: string) => {
    form.setValue("project_class_id", project_class_id); // Set project class ID

    // Fetch and filter project types based on selected project class
    getProjectTypesForClass(project_class_id);

    // Clear project_type_id on project class change
    form.setValue("project_type_id", "");
  };

  useEffect(() => {
    if (lead?.project_class_id) {
      // If there's a pre-selected project class in the lead, fetch corresponding project types
      getProjectTypesForClass(lead.project_class_id[0]?.toString());
    }
  }, [lead?.project_class_id, getProjectTypesForClass]);

  useEffect(() => {
    const callToMaster = async () => {
      const params: Partial<MasterDataParam> = {
        countries: true,
        provinces: true,
        projectClasses: true,
        projectTypes: true,
        projectStatuses: true,
        regions: true,
        counties: true,
        cities: true,
      };
      const data = await getMasterData(params);
      console.log("master data", data);
      setMasterData(data);
    };

    const callGetStage = async () => {
      if (isCreateMode) {
        const response = await getStages();

        setStages(response.stages);
      }
    };

    callGetStage();
    callToMaster();
  }, []);

  const provinceId = form.watch("province_id");

  useEffect(() => {
    if (stages && stages.length > 0) {
      const st = searchParams.get("stage");
      console.log("stage", st);
      if (st) form.setValue("stage_id", st);
      console.log(form.getValues());
    }
  }, [stages, searchParams]);

  const getProvinceOptions = useCallback(async (country_id: string) => {
    const d = await getMasterData({
      countryId: Number(country_id),
      provinces: true,
    });
    if (d && d.provinces) setProvinceOptions(d.provinces);
    else {
      setProvinceOptions([]);
    }
  }, []);

  useEffect(() => {
    // Fetch the province options when the country changes
    if (lead?.country_id) {
      getProvinceOptions(lead.country_id[0]?.toString());
    }
  }, [lead?.country_id, getProvinceOptions]);

  const getCitiesOptions = useCallback(async (county_id: string) => {
    const d = await getMasterData({
      countyId: Number(county_id),
      cities: true,
    });
    if (d && d.cities) setCityOptions(d.cities);
    else {
      setCityOptions([]);
    }
  }, []);

  useEffect(() => {
    // Fetch the province options when the country changes
    if (lead?.county_id) {
      getCitiesOptions(lead.county_id[0]?.toString());
    }
  }, [lead?.country_id, getProvinceOptions]);

  //for prefill county and refion in edit mode
  // useEffect(() => {
  //   if (isEditMode) {
  //     if (
  //       lead?.region_id &&
  //       regionOptions?.some((opt) => opt.id === lead.region_id[0])
  //     ) {
  //       form.setValue("region_id", lead.region_id[0].toString());
  //     }

  //     if (
  //       lead?.county_id &&
  //       countyOptions?.some((opt) => opt.id === lead.county_id[0])
  //     ) {
  //       form.setValue("county_id", lead.county_id[0].toString());
  //     }
  //   }
  // }, [regionOptions, countyOptions, lead?.region_id, lead?.county_id, isEditMode]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    console.log("Submitting Form Data:", values);
    setLoading(true);

    let filteredValues: Record<string, any>;

    if (isEditMode && lead) {
      // In edit mode, keep empty strings so cleared fields are submitted
      filteredValues = Object.fromEntries(
        Object.entries(values).filter(([, v]) => v !== undefined)
      );

      // Default empty numeric fields to 0 in edit mode
      const defaultToZeroFields = [
        "expected_revenue",
        "cost_of_construction",
        "latitude",
        "longitude",
      ];

      for (const key of defaultToZeroFields) {
        if (filteredValues[key] === "") {
          filteredValues[key] = 0;
        }
      }
    } else {
      // In add mode, skip empty strings and undefined values
      filteredValues = Object.fromEntries(
        Object.entries(values).filter(([, v]) => v !== "" && v !== undefined)
      );
      console.log("line 706", filteredValues);
    }

    // Cast number fields
    const castNumberFields = [
      "expected_revenue",
      "cost_of_construction",
      "stage_id",
      "region_id",
      "county_id",
      "project_class_id",
      "project_type_id",
      "project_status_id",
      "country_id",
      "province_id",
      "city_id",
      "latitude",
      "longitude",
    ];

    for (const key of castNumberFields) {
      if (filteredValues[key] === "") {
        // In edit mode, send null instead of 0 for empty dropdowns
        if (isEditMode) {
          filteredValues[key] = 0;
        } else {
          delete filteredValues[key]; // In add mode, remove them
        }
      } else if (filteredValues[key] !== undefined) {
        filteredValues[key] = Number(filteredValues[key]);
      }
    }
    console.log("line 735", filteredValues);

    let updates = filteredValues;

    if (isEditMode && lead) {
      updates = Object.fromEntries(
        Object.entries(filteredValues).filter(
          ([key, value]) =>
            String((lead as Record<string, any>)[key] ?? "") !== String(value)
        )
      );

      // Optionally exclude unchanged coordinates (as string)
      if (
        updates.longitude !== undefined &&
        String(updates.longitude) === String(lead.longitude)
      ) {
        delete updates.longitude;
      }
      if (
        updates.latitude !== undefined &&
        String(updates.latitude) === String(lead.latitude)
      ) {
        delete updates.latitude;
      }
    }

    if (Object.keys(updates).length === 0) {
      console.log("No changes detected. Skipping update.");
      setLoading(false);
      return;
    }

    console.log(
      "Final Payload to Send:",
      isEditMode ? updates : filteredValues
    );

    try {
      let res;
      if (isCreateMode) {
        res = await createLead(filteredValues);
        console.log("Lead Created:", res);
        if (res?.statusCode) {
          toastService.success("Lead created successfully");
        }
      } else if (isEditMode) {
        const numId = Number(id);
        res = await editLead(numId, updates);
        console.log("Lead Updated:", res);
        if (res?.statusCode) {
          toastService.success("Lead updated successfully");
        }
      }

      navigate(-1);
    } catch (error) {
      console.error("Error submitting Lead:", error);
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   // Watch for changes to country_id and reset province_id
  //   const countryId = form.watch("country_id");

  //   if (!countryId) {
  //     // If country_id is not selected, reset the province_id
  //     form.setValue("province_id", ""); // This will reset the province field to show the placeholder
  //   }
  // }, [form.watch("country_id")]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 p-6 relative"
      >
        <div className="w-full flex items-center justify-between">
          <div className="min-w-[180px]">
            {isCreateMode && (
              <FormField
                control={form.control}
                name="stage_id"
                render={({ field }) => (
                  <FormItem>
                    {/* <FormLabel className={formLabelStyle} >User</FormLabel> */}
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        const selectedStage = stages.find(
                          (st) => st.id.toString() === value
                        );
                        if (selectedStage) {
                          form.setValue(
                            "stage_id",
                            selectedStage.id.toString()
                          );
                        }
                      }}
                      value={form.watch("stage_id") || ""}
                    >
                      <FormControl className="h-[40px]">
                        <SelectTrigger>
                          <SelectValue placeholder="Select Project Stage" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {stages &&
                          stages.map((st) => (
                            <SelectItem key={st.id} value={st.id.toString()}>
                              {st.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          <div className="flex gap-2">
            <Button size="sm" className="px-[2rem] !bg-charcoal">
              {isEditMode ? "Update" : "Create"}
            </Button>

            <Button
              size="sm"
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="border-charcoal"
            >
              Cancel
            </Button>
          </div>
        </div>

        <div className="space-y-4 border rounded-md p-3">
          <div className="">
            <TypographyH2 className="!text-[16px] font-medium leading-[24px] text-charcoal tracking-wide uppercase">
              Basic Information
            </TypographyH2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="col-span-full">
                    <FormControl>
                      {isEditMode ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Input
                              placeholder="Lead title e.g. ABC *"
                              className="text-lg border-t-0 border-x-0 rounded-none font-medium"
                              value={field.value}
                              disabled
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>You cannot edit this field</p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Input
                          placeholder="Lead title e.g. ABC *"
                          className="text-lg border-t-0 border-x-0 rounded-none font-medium"
                          {...field}
                        />
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cost_of_construction"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>
                      Cost of Construction ($)
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Cost of Construction $"
                        {...field}
                        type="number"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expected_revenue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>
                      Expected Revenue ($)
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Expected Revenue $"
                        {...field}
                        type="number"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="permit_issue_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>
                      Permit Issue Date
                    </FormLabel>
                    <div>
                      <Input
                        className="w-full"
                        type="date"
                        {...field}
                        value={
                          field.value
                            ? new Date(field.value).toISOString().split("T")[0]
                            : "Select Date"
                        }
                      />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="">
            <TypographyH2 className="!text-[16px] font-medium tracking-wide uppercase">
              Project Details
            </TypographyH2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>Phone</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}

              <FormField
                control={form.control}
                name="country_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>Country </FormLabel>
                    <Select
                      onValueChange={(selectedValue) => {
                        if (selectedValue === "clear") {
                          field.onChange("");
                          form.setValue("province_id", "");
                          form.setValue("region_id", "");
                          form.setValue("county_id", "");
                          form.setValue("city_id", "");
                          getProvinceOptions("");
                        } else {
                          console.log("country_id changed", selectedValue);
                          field.onChange(selectedValue);
                          form.setValue("province_id", "");
                          form.setValue("region_id", "");
                          form.setValue("county_id", "");
                          form.setValue("city_id", "");
                          getProvinceOptions(selectedValue);
                        }
                      }}
                      value={field.value || ""}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="max-h-[200px] overflow-y-auto">
                        <SelectItem
                          value="clear"
                          className="text-red-500 italic"
                        >
                          Clear selection
                        </SelectItem>
                        {masterData && masterData.countries ? (
                          masterData.countries.map((c) => (
                            <SelectItem key={c.id} value={c.id + ""}>
                              {c.display_name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="NA">No data available</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="province_id"
                render={({ field }) => (
                  <FormItem className="mt-1">
                    <FormLabel className={formLabelStyle}>
                      <div className="flex items-center gap-1">
                        <span>Province</span>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-pointer">
                                <IoIosInformationCircle />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              Select The Country First.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </FormLabel>
                    <Select
                      onValueChange={(selectedValue) => {
                        if (selectedValue === "clear") {
                          field.onChange("");
                          form.setValue("region_id", "");
                          form.setValue("county_id", "");
                          form.setValue("city_id", "");
                          getRegionsForProvince("");
                        } else {
                          field.onChange(selectedValue);
                          form.setValue("region_id", "");
                          form.setValue("county_id", "");
                          form.setValue("city_id", "");
                          getRegionsForProvince(selectedValue);
                        }
                      }}
                      value={field.value || ""}
                    >
                      <FormControl>
                        <SelectTrigger
                          onMouseDown={(e) => {
                            if (!form.watch("country_id")) {
                              e.preventDefault(); // Block opening if no Country is selected
                            }
                          }}
                          className={
                            !form.watch("country_id")
                              ? "cursor-not-allowed opacity-50"
                              : ""
                          }
                        >
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                      </FormControl>

                      <SelectContent className="max-h-[200px] overflow-y-auto">
                        <SelectItem
                          value="clear"
                          className="text-red-500 italic"
                        >
                          Clear selection
                        </SelectItem>
                        {provinceOptions && provinceOptions.length > 0 ? (
                          provinceOptions.map((province) => (
                            <SelectItem
                              key={province.id}
                              value={province.id + ""}
                            >
                              {province.name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="NA" disabled>
                            No data available
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>

                    <FormMessage />
                  </FormItem>
                )}
              />

              {user?.countryName === "United States" && (
                <FormField
                  control={form.control}
                  name="region_id"
                  render={({ field }) => (
                    <FormItem className="mt-1">
                      <FormLabel className={formLabelStyle}>
                        <div className="flex items-center gap-1">
                          <span>Region</span>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-pointer">
                                  <IoIosInformationCircle />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                Select The Region.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </FormLabel>
                      <Select
                        onValueChange={(regionId) => {
                          if (regionId === "clear") {
                            field.onChange("");
                            form.setValue("county_id", "");
                            form.setValue("city_id", "");
                            return;
                          }

                          field.onChange(regionId);
                          form.setValue("county_id", "");
                          form.setValue("city_id", "");

                          const provinceId = form.getValues("province_id");
                          if (provinceId) {
                            getCountiesForProvinceAndRegion(
                              provinceId,
                              regionId
                            );
                          }
                        }}
                        value={field.value || ""}
                      >
                        <FormControl>
                          <SelectTrigger
                            onMouseDown={(e) => {
                              if (!form.watch("country_id")) {
                                e.preventDefault();
                              }
                            }}
                            className={
                              !form.watch("country_id")
                                ? "cursor-not-allowed opacity-50"
                                : ""
                            }
                          >
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="max-h-[200px] overflow-y-auto">
                          <SelectItem
                            value="clear"
                            className="text-red-500 italic"
                          >
                            Clear selection
                          </SelectItem>

                          {regionOptions && regionOptions.length > 0 ? (
                            regionOptions.map((region) => (
                              <SelectItem
                                key={region.id}
                                value={region.id.toString()}
                              >
                                {region.name}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="NA" disabled>
                              No data available
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {user?.countryName === "United States" && (
                <FormField
                  control={form.control}
                  name="county_id"
                  render={({ field }) => {
                    const regionSelected = !!form.watch("region_id");

                    return (
                      <FormItem className="mt-1" >
                        <FormLabel className={formLabelStyle}>
                          <div className="flex items-center gap-1">
                            <span>County</span>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="cursor-pointer">
                                    <IoIosInformationCircle />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Select The County.
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </FormLabel>

                        <Select
                          onValueChange={(selectedValue) => {
                            if (selectedValue === "clear") {
                              field.onChange("");
                              getCitiesOptions("");
                            } else {
                              field.onChange(selectedValue);

                              form.setValue("city_id", "");
                              getCitiesOptions(selectedValue);
                            }
                          }}
                          value={field.value || ""}
                          disabled={!regionSelected}
                        >
                          <FormControl>
                            <SelectTrigger
                              onMouseDown={(e) => {
                                if (!regionSelected) e.preventDefault();
                              }}
                              className={
                                !regionSelected
                                  ? "cursor-not-allowed opacity-50"
                                  : ""
                              }
                            >
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                          </FormControl>

                          <SelectContent className="max-h-[200px] overflow-y-auto">
                            <SelectItem
                              value="clear"
                              className="text-red-500 italic"
                            >
                              Clear selection
                            </SelectItem>

                            {countyOptions && countyOptions.length > 0 ? (
                              countyOptions.map((county) => (
                                <SelectItem
                                  key={county.id}
                                  value={county.id.toString()}
                                >
                                  {county.name}
                                </SelectItem>
                              ))
                            ) : (
                              <SelectItem value="NA" disabled>
                                No data available
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              )}

              <FormField
                control={form.control}
                name="city_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>City</FormLabel>
                    <Select
                      onValueChange={(selectedValue) => {
                        if (selectedValue === "clear") {
                          field.onChange("");
                        } else {
                          field.onChange(selectedValue);
                        }
                      }}
                      value={field.value || ""}
                    >
                      <FormControl>
                        <SelectTrigger
                          onMouseDown={(e) => {
                            if (!provinceId) {
                              e.preventDefault(); // Prevent open if no province
                            }
                          }}
                          className={
                            !provinceId ? "cursor-not-allowed opacity-50" : ""
                          }
                        >
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="max-h-[200px] overflow-y-auto">
                        <SelectItem
                          value="clear"
                          className="text-red-500 italic"
                        >
                          Clear selection
                        </SelectItem>
                        {cityOptions.length > 0 ? (
                          cityOptions.map((city) => (
                            <SelectItem
                              key={city.id}
                              value={city.id.toString()}
                            >
                              {city.name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="NA" disabled>
                            No cities found
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>Address </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="latitude"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>Latitude </FormLabel>
                    <FormControl>
                      {/* <Input placeholder="0.000" {...field} type="number" /> */}
                      <Input
                        placeholder="0.000"
                        {...field}
                        type="text"
                        inputMode="decimal"
                        pattern="^-?\d*\.?\d*$"
                        onKeyDown={(e) => {
                          const allowedKeys = [
                            "Backspace",
                            "Tab",
                            "ArrowLeft",
                            "ArrowRight",
                            "Delete",
                            "Home",
                            "End",
                            "-", // for negative numbers
                            ".", // for decimal point
                          ];
                          const isNumber = e.key >= "0" && e.key <= "9";

                          if (!isNumber && !allowedKeys.includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="longitude"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>Longitude</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="0.000"
                        {...field}
                        type="text"
                        inputMode="decimal"
                        pattern="^-?\d*\.?\d*$"
                        onKeyDown={(e) => {
                          const allowedKeys = [
                            "Backspace",
                            "Tab",
                            "ArrowLeft",
                            "ArrowRight",
                            "Delete",
                            "Home",
                            "End",
                            "-", // for negative numbers
                            ".", // for decimal point
                          ];
                          const isNumber = e.key >= "0" && e.key <= "9";

                          if (!isNumber && !allowedKeys.includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="project_description"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel className={formLabelStyle}>
                      Project Description
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter project description"
                        className="resize-none"
                        {...field}
                        value={field.value}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="">
            <TypographyH2 className="!text-[16px] font-medium tracking-wide uppercase">
              Category and Project Stage
            </TypographyH2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="project_class_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>
                      Project Class
                    </FormLabel>
                    <Select
                      onValueChange={(value) => {
                        if (value === "clear") {
                          field.onChange("");
                          handleProjectClassChange("");
                        } else {
                          field.onChange(value);
                          handleProjectClassChange(value);
                        }
                      }}
                      value={field.value || ""}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="max-h-[200px] overflow-y-auto">
                        <SelectItem
                          value="clear"
                          className="text-red-500 italic"
                        >
                          Clear selection
                        </SelectItem>
                        {masterData && masterData?.projectClasses?.length ? (
                          masterData.projectClasses.map((pc) => (
                            <SelectItem key={pc.id} value={pc.id + ""}>
                              {pc.name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="NA">No data available</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="project_type_id"
                render={({ field }) => (
                  <FormItem className="mt-1">
                    <FormLabel className={formLabelStyle}>
                      <div className="flex items-center gap-1 ">
                        <span>Project Type</span>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-pointer">
                                <IoIosInformationCircle />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              Select project class first.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </FormLabel>
                    <Select
                      onValueChange={(selectedValue) => {
                        if (selectedValue === "clear") {
                          field.onChange("");
                        } else {
                          field.onChange(selectedValue);
                        }
                      }}
                      value={field.value || ""}
                    >
                      <FormControl>
                        <SelectTrigger
                          onMouseDown={(e) => {
                            if (!form.watch("project_class_id")) {
                              e.preventDefault(); // Block opening
                            }
                          }}
                          className={
                            !form.watch("project_class_id")
                              ? "cursor-not-allowed opacity-50"
                              : ""
                          }
                        >
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="max-h-[200px] overflow-y-auto">
                        <SelectItem
                          value="clear"
                          className="text-red-500 italic"
                        >
                          Clear selection
                        </SelectItem>
                        {filteredProjectTypes &&
                        filteredProjectTypes.length > 0 ? (
                          filteredProjectTypes.map((pt) => (
                            <SelectItem key={pt.id} value={pt.id + ""}>
                              {pt.name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="NA" disabled>
                            No data available
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="project_status_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>
                      Project Status (optional)
                    </FormLabel>
                    <Select
                      onValueChange={(selectedValue) => {
                        if (selectedValue === "clear") {
                          field.onChange("");
                        } else {
                          field.onChange(selectedValue);
                        }
                      }}
                      value={field.value || ""}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="max-h-[200px] overflow-y-auto">
                        <SelectItem
                          value="clear"
                          className="text-red-500 italic"
                        >
                          Clear selection
                        </SelectItem>
                        {masterData && masterData.projectStatuses ? (
                          masterData.projectStatuses.map((ps) => (
                            <SelectItem key={ps.id} value={ps.id + ""}>
                              {ps.name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="NA">No data available</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          <div className="">
            <TypographyH2 className="!text-[16px] font-medium leading-[24px] text-charcoal tracking-wide uppercase">
              Owner Details (optional)
            </TypographyH2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="owner_company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>
                      Owner Company
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Enter Company" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="owner_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>Owner Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="owner_phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>
                      Owner Phone
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Enter Phone" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="owner_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>
                      Owner Email
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Enter Email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="">
            <TypographyH2 className="!text-[16px] font-medium leading-[24px] text-charcoal tracking-wide uppercase">
              Applicant Details (optional)
            </TypographyH2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="applicant_company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>
                      applicant Company
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Enter Company" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="applicant_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>
                      applicant Name
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Enter Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="applicant_phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>
                      applicant Phone
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Enter Phone" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="applicant_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={formLabelStyle}>
                      applicant Email
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Enter Email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* <div className="">
            <TypographyH2 className="!text-[16px] font-medium leading-[24px] text-charcoal tracking-wide uppercase">
              Product Image URLs
            </TypographyH2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ImageUrlForm imageUrls={imageUrls} setImageUrls={setImageUrls} />
            </div>
          </div> */}

          <div className="">
            <TypographyH2 className="!text-[16px] font-medium leading-[24px] text-charcoal tracking-wide uppercase">
              Additional Information
            </TypographyH2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="additional_info"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel className={formLabelStyle}>
                      Additional Info (If any)
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter project description"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
};

export default AddLeadForm;
